import type {
	CloudLatestImage,
	CloudLatestImageData,
} from 'src/components/Camera/types/cloud-recordings';
import { get, writable } from 'svelte/store';

/**
 * Normalizes a raw per-device thumbnail entry into a {@link CloudLatestImage},
 * carrying the `encrypted` flag and the recording id (used to derive the
 * per-recording decryption key). Accepts both the `{ encrypted, recordingId,
 * imageUrls }` shape and the legacy flat shape.
 *
 * @param entry - The raw thumbnail entry from the API.
 * @returns The normalized preview, or `null` when there are no usable URLs.
 */
const normalizeThumbnailEntry = (entry: any): CloudLatestImage | null => {
	if (!entry) return null;
	const images = entry.imageUrls ?? entry;
	if (!images?.large && !images?.medium && !images?.small) return null;
	return { ...images, encrypted: !!entry.encrypted, recId: entry.recordingId };
};

function create_camera_previews() {
	const { subscribe, set, update } = writable<CloudLatestImageData>({});

	return {
		subscribe,
		set,
		update,
		clear: () => set({}),
		/**
		 * Normalizes a raw `deviceId -> thumbnail entry` map from the API and
		 * replaces the store with the resulting previews.
		 *
		 * @param raw - The raw thumbnail map keyed by device id.
		 * @returns The normalized previews that were stored.
		 */
		setFromRaw: (raw: Record<string, unknown>): CloudLatestImageData => {
			const previews: CloudLatestImageData = {};
			for (const [deviceId, entry] of Object.entries(raw ?? {})) {
				const normalized = normalizeThumbnailEntry(entry);
				if (normalized) previews[deviceId] = normalized;
			}
			set(previews);
			return previews;
		},
		updateDevicePreview: (deviceId: string, previewData: CloudLatestImage) => {
			update((currentPreviews) => {
				return {
					...currentPreviews,
					[deviceId]: previewData,
				};
			});
		},
		/**
		 * Subscribes to changes of a single device's latest recording. The callback
		 * fires only when *that device's* preview points to a new recording — the
		 * current value is used as the baseline, so it does not fire on subscribe
		 * (do the initial load separately).
		 *
		 * @param deviceId - The device whose latest recording to watch.
		 * @param cb - Invoked with the new recording id whenever it changes.
		 * @returns The unsubscribe function.
		 */
		onDeviceRecordingChange: (
			deviceId: string,
			cb: (recId: string | undefined) => void,
		): (() => void) => {
			let lastRecId = get({ subscribe })[deviceId]?.recId;
			return subscribe((previews) => {
				const recId = previews[deviceId]?.recId;
				if (recId !== lastRecId) {
					lastRecId = recId;
					cb(recId);
				}
			});
		},
	};
}

export const camera_previews = create_camera_previews();
